import React from "react";
import {
  Crown, Sparkles, Check, X, Heart, Eye, Search, Calendar,
  RefreshCw, Filter, BookOpen, ExternalLink, ShieldCheck,
} from "lucide-react";

/** Static marketing/explainer sections of the Premium landing page.
 *  Pulled out of Premium.jsx to keep the page's data/state logic focused
 *  on the actual purchase flow.  No props beyond the FAQ/comparison data —
 *  these are pure-static blocks. */

export const COMPARISON = [
  { feat: "Questionário de 45 perguntas", free: true, premium: true },
  { feat: "Discover ilimitado por afinidade", free: true, premium: true },
  { feat: "Chat com ligações mútuas", free: true, premium: true },
  { feat: "Diário de encontros (privado + partilhado)", free: true, premium: true },
  { feat: "Score de compatibilidade", free: true, premium: true },
  { feat: "Likes por dia", free: "5/dia", premium: "Ilimitados" },
  { feat: "Fichas de descoberta profunda (perfil-a-perfil)", free: "3 + bónus", premium: "Não precisas", icon: Sparkles, highlight: true },
  { feat: "Filtros avançados (afinidade mínima, distância, idade)", free: false, premium: true,
    highlight: true, icon: Filter },
  { feat: "Análise IA detalhada do par (Claude Sonnet)", free: "Teaser ou via ficha", premium: "Sempre",
    highlight: true, icon: Sparkles },
  { feat: "Re-gerar análise IA com novos ângulos", free: false, premium: "3/mês",
    highlight: true, icon: RefreshCw },
  { feat: "Perguntas IA para o 1º encontro", free: "Via ficha", premium: "Sempre",
    highlight: true, icon: Calendar },
  { feat: "Quem te deu like (lista geral)", free: false, premium: true,
    highlight: true, icon: Heart },
  { feat: "Quem viu o teu perfil", free: false, premium: true,
    highlight: true, icon: Eye },
  { feat: "Pesquisa pública RGPD-compliant (Tavily)", free: "Via ficha", premium: "Sempre",
    highlight: true, icon: Search },
  { feat: "Selo Premium visível no perfil", free: false, premium: true, icon: Crown },
  { feat: "Acesso prioritário a novidades", free: false, premium: true },
];

export const FAQ = [
  { q: "O que são as Fichas de Descoberta Profunda?",
    a: "Cada conta nova recebe 3 fichas grátis (mais bónus por perfil completo e por primeiro match). Cada ficha gasta numa pessoa desbloqueia para sempre, só para ti, a análise IA completa do par, as perguntas para o 1º encontro e a pesquisa pública (se a pessoa autorizou). Permite-te avaliar com profundidade até 5 candidatos antes de decidires se queres subscrever Premium para uso ilimitado." },
  { q: "É renovação automática?",
    a: "Não. Os planos Soulmatch são compras únicas (1 mês ou 1 ano). No fim do período recebes um lembrete e decides se queres renovar. Não há cobranças recorrentes sem a tua autorização explícita." },
  { q: "Posso cancelar?",
    a: "Não há nada para cancelar — não há renovação automática. O acesso Premium expira sozinho no fim do período que compraste, sem custos adicionais." },
  { q: "Quem emite a fatura?",
    a: "A Solfut, Lda — emitimos automaticamente Fatura+Recibo via Moloni em cada compra, com NIF se forneceres ou em \"Consumidor Final\" caso contrário. Disponíveis na zona Faturação." },
  { q: "Posso ter reembolso?",
    a: "Sim — temos política de cortesia de 14 dias se não tiveres usado funcionalidades Premium pagas." },
  { q: "Os meus dados estão seguros?",
    a: "Pagamentos processados pela EuPago (certificado SIBS · PSD2). Não armazenamos cartões. Encriptação TLS em todas as comunicações." },
];

const cell = (v) => {
  if (v === true) return <Check className="w-4 h-4 text-emerald-300 mx-auto" />;
  if (v === false) return <X className="w-4 h-4 text-[#A29F9B]/40 mx-auto" />;
  return <span className="text-xs text-[#DCD7D2] font-medium">{v}</span>;
};

export const TokensExplainer = () => (
  <section id="fichas" className="mb-20 bg-gradient-to-br from-[#C5A880]/10 to-[#161920] border border-[#C5A880]/30 rounded-2xl p-8 md:p-12" data-testid="tokens-explainer">
    <div className="flex items-center gap-2 mb-3">
      <Sparkles className="w-5 h-5 text-[#C5A880]" />
      <span className="label-uppercase">Conta gratuita</span>
    </div>
    <h2 className="font-display text-3xl md:text-4xl mb-4">3 fichas de descoberta profunda<br/><span className="text-[#C5A880]">grátis para sempre</span></h2>
    <p className="text-[#DCD7D2] leading-relaxed max-w-3xl mb-8 text-lg">
      Antes de subscrever, dás-te o luxo de testar a fundo até <strong>5 perfis específicos</strong> à tua escolha
      — análise IA completa, perguntas para o 1º encontro e pesquisa pública incluídas. Cada ficha gasta
      desbloqueia um perfil <strong>para sempre</strong>, mesmo que nunca chegues a ser Premium.
    </p>
    <div className="grid sm:grid-cols-3 gap-4 mb-8">
      <div className="bg-[#0C0E12]/60 border border-white/10 rounded-2xl p-5">
        <div className="font-display text-3xl text-[#C5A880] mb-1">3</div>
        <h3 className="font-medium text-[#F5F2EE] mb-1">Iniciais</h3>
        <p className="text-xs text-[#A29F9B] leading-relaxed">Após completares o questionário (45 perguntas).</p>
      </div>
      <div className="bg-[#0C0E12]/60 border border-white/10 rounded-2xl p-5">
        <div className="font-display text-3xl text-[#C5A880] mb-1">+1</div>
        <h3 className="font-medium text-[#F5F2EE] mb-1">Bónus de perfil</h3>
        <p className="text-xs text-[#A29F9B] leading-relaxed">Adicionas 3 fotos + bio com pelo menos 200 caracteres.</p>
      </div>
      <div className="bg-[#0C0E12]/60 border border-white/10 rounded-2xl p-5">
        <div className="font-display text-3xl text-[#C5A880] mb-1">+1</div>
        <h3 className="font-medium text-[#F5F2EE] mb-1">Bónus de match</h3>
        <p className="text-xs text-[#A29F9B] leading-relaxed">Recebes na tua primeira ligação mútua.</p>
      </div>
    </div>
    <div className="border-t border-white/10 pt-6">
      <h3 className="font-medium text-[#F5F2EE] mb-3 inline-flex items-center gap-2">
        <Crown className="w-4 h-4 text-[#C5A880]" /> Quando passar a Premium?
      </h3>
      <p className="text-sm text-[#A29F9B] leading-relaxed max-w-3xl">
        Quando perceberes que queres olhar com profundidade para <strong>mais de 5 pessoas</strong>, ou
        quando quiseres ferramentas horizontais — <em>ver quem te deu like sem ter de fazer match</em>,
        <em> ver quem visitou o teu perfil</em>, <em>filtros por afinidade mínima/distância</em> e
        <em> re-gerar a análise IA com novos ângulos</em>. As fichas são para começar consciente; o
        Premium é para abraçar a busca a sério.
      </p>
    </div>
  </section>
);

export const ComparisonTable = () => (
  <section className="mb-20" data-testid="comparison-table">
    <h2 className="font-display text-3xl md:text-4xl text-center mb-10">O que muda com Premium</h2>
    <div className="bg-[#161920] border border-white/5 rounded-2xl overflow-hidden">
      <div className="grid grid-cols-[1fr_100px_120px] md:grid-cols-[1fr_140px_160px] py-4 px-6 border-b border-white/10 text-xs uppercase tracking-wider text-[#A29F9B]">
        <span>Funcionalidade</span>
        <span className="text-center">Free</span>
        <span className="text-center text-[#C5A880]">Premium</span>
      </div>
      {COMPARISON.map((row, idx) => {
        const Icon = row.icon;
        return (
          <div key={row.feat}
            className={`grid grid-cols-[1fr_100px_120px] md:grid-cols-[1fr_140px_160px] py-4 px-6 items-center text-sm ${
              idx < COMPARISON.length - 1 ? "border-b border-white/5" : ""
            } ${row.highlight ? "bg-[#C5A880]/5" : ""}`}>
            <span className="text-[#F5F2EE] inline-flex items-center gap-2">
              {Icon && <Icon className="w-4 h-4 text-[#C5A880] shrink-0" />}
              {row.feat}
            </span>
            <span className="text-center">{cell(row.free)}</span>
            <span className="text-center">{cell(row.premium)}</span>
          </div>
        );
      })}
    </div>
  </section>
);

export const AffinityTiers = () => (
  <section className="mb-20 bg-[#161920] border border-white/5 rounded-2xl p-8" data-testid="affinity-explainer">
    <h2 className="font-display text-2xl mb-6">Como interpretamos a afinidade</h2>
    <div className="grid sm:grid-cols-3 gap-4">
      <div className="p-5 rounded-2xl bg-[#C5A880]/15 border border-[#C5A880]">
        <span className="font-display text-3xl text-[#C5A880]">≥ 80%</span>
        <h3 className="font-medium text-[#F5F2EE] mt-1">Grande afinidade</h3>
        <p className="text-xs text-[#A29F9B] mt-2 leading-relaxed">Alinhamento profundo de valores, traços e expectativas. Vale o investimento.</p>
      </div>
      <div className="p-5 rounded-2xl bg-amber-400/10 border border-amber-400/40">
        <span className="font-display text-3xl text-amber-300">50–79%</span>
        <h3 className="font-medium text-[#F5F2EE] mt-1">Média afinidade</h3>
        <p className="text-xs text-[#A29F9B] mt-2 leading-relaxed">Pontes possíveis com curiosidade e diferença gerida. Encontro pode surpreender.</p>
      </div>
      <div className="p-5 rounded-2xl bg-white/5 border border-white/15">
        <span className="font-display text-3xl text-[#A29F9B]">&lt; 50%</span>
        <h3 className="font-medium text-[#F5F2EE] mt-1">Baixa afinidade</h3>
        <p className="text-xs text-[#A29F9B] mt-2 leading-relaxed">Visões e ritmos divergentes. Possível, mas exige consciência mútua.</p>
      </div>
    </div>
  </section>
);

export const WorkshopCoachingCta = () => (
  <section className="grid md:grid-cols-2 gap-5 mb-20">
    <a href="https://ihavethepower.net/loja/cursos-em-video/workshop-o-que-ninguem-te-ensinou-sobre-relacoes-video-online-com-adelino-cunha/"
      target="_blank" rel="noopener noreferrer"
      className="p-6 rounded-2xl bg-[#161920] border border-[#C5A880]/30 hover:border-[#C5A880] transition-colors block group"
      data-testid="premium-workshop">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-[#C5A880]" />
        <span className="label-uppercase">Workshop</span>
      </div>
      <h3 className="font-display text-xl text-[#F5F2EE] group-hover:text-[#C5A880] transition-colors leading-tight">
        "O que ninguém te ensinou sobre Relações"
      </h3>
      <p className="text-sm text-[#A29F9B] mt-2 mb-4 leading-relaxed">
        Workshop em vídeo com Adelino Cunha — junta neurociência, psicologia e prática.
      </p>
      <span className="inline-flex items-center gap-1.5 text-xs text-[#C5A880]">
        Aceder <ExternalLink className="w-3 h-3" />
      </span>
    </a>

    <a href="https://ihavethepower.net/loja/coaching/life-coaching-com-coach-ihtp/"
      target="_blank" rel="noopener noreferrer"
      className="p-6 rounded-2xl bg-[#161920] border border-[#C5A880]/30 hover:border-[#C5A880] transition-colors block group"
      data-testid="premium-coaching">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
        <span className="label-uppercase">Coaching 1-a-1</span>
      </div>
      <h3 className="font-display text-xl text-[#F5F2EE] group-hover:text-[#C5A880] transition-colors leading-tight">
        Acompanhamento personalizado
      </h3>
      <p className="text-sm text-[#A29F9B] mt-2 mb-4 leading-relaxed">
        Sessões individuais para validação de candidato(a) e preparação do 1º encontro.
      </p>
      <span className="inline-flex items-center gap-1.5 text-xs text-[#C5A880]">
        Saber mais <ExternalLink className="w-3 h-3" />
      </span>
    </a>
  </section>
);

export const PremiumFaq = () => (
  <section className="mb-20" data-testid="premium-faq">
    <h2 className="font-display text-3xl text-center mb-10">Perguntas frequentes</h2>
    <div className="space-y-3 max-w-3xl mx-auto">
      {FAQ.map((f, idx) => (
        <details key={f.q} className="bg-[#161920] border border-white/5 rounded-2xl p-5 group"
          data-testid={`faq-${idx}`}>
          <summary className="cursor-pointer text-[#F5F2EE] font-medium list-none flex items-center justify-between">
            {f.q}
            <span className="text-[#C5A880] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
          </summary>
          <p className="text-sm text-[#A29F9B] mt-3 leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  </section>
);
